import { z } from 'zod';

const webViewParamsSchema = z.object({
  // Token is optional in URL — it will be received via postMessage from Flutter.
  // Kept as optional for backward compatibility (dev/testing can still pass via URL).
  token: z.string().optional(),
  incident_id: z.string().min(1, 'incident_id is required'),
  item_id: z.string().optional(),
  item_code: z.string().optional(),
  item_name: z.string().optional(),
  serial_no: z.string().min(1, 'serial_no is required'),
  technician_id: z.string().min(1, 'technician_id is required'),
  technician_name: z.string().min(1, 'technician_name is required'),
  customer_name: z.string().optional(),
  customer_phone: z.string().optional(),
  customer_address: z.string().optional(),
}).refine(
  (data) => (data.item_id && data.item_id.length > 0) || (data.item_code && data.item_code.length > 0),
  { message: 'Either item_id or item_code is required' }
);

export type WebViewParams = z.infer<typeof webViewParamsSchema>;
export { webViewParamsSchema };

export class MissingParamsError extends Error {
  public readonly missingFields: string[];
  public readonly partialParams: Partial<WebViewParams>;

  constructor(
    missingFields: string[],
    partialParams: Partial<WebViewParams>
  ) {
    const fields = missingFields.join(', ');
    super(`Missing required URL params: ${fields}`);
    this.name = 'MissingParamsError';
    this.missingFields = missingFields;
    this.partialParams = partialParams;
  }

  get isMissingToken(): boolean {
    return this.missingFields.includes('token');
  }

  get isMissingIncidentContext(): boolean {
    const hasItemId = this.partialParams.item_id && String(this.partialParams.item_id).trim().length > 0;
    const hasItemCode = this.partialParams.item_code && String(this.partialParams.item_code).trim().length > 0;
    return this.missingFields.some(f =>
      ['incident_id', 'serial_no', 'technician_id', 'technician_name'].includes(f)
    ) || (!hasItemId && !hasItemCode);
  }
}

export function parseUrlParams(): WebViewParams {
  const params = new URLSearchParams(window.location.search);
  const raw = {
    token: params.get('token') || undefined,
    incident_id: params.get('incident_id') ?? '',
    item_id: params.get('item_id') || undefined,
    item_code: params.get('item_code') || undefined,
    item_name: params.get('item_name') || undefined,
    serial_no: params.get('serial_no') ?? '',
    technician_id: params.get('technician_id') ?? '',
    technician_name: params.get('technician_name') ?? '',
    customer_name: params.get('customer_name') ?? undefined,
    customer_phone: params.get('customer_phone') ?? undefined,
    customer_address: params.get('customer_address') ?? undefined,
  };

  const result = webViewParamsSchema.safeParse(raw);

  if (!result.success) {
    const missing = result.error.errors.map(e => {
      // Refine-level errors have no path, use message directly
      if (e.path.length === 0) return e.message;
      // Field-level errors: extract the field name for backwards compat
      return e.path.join('.');
    });
    throw new MissingParamsError(missing, raw);
  }

  return result.data;
}

export function tryParseUrlParams():
  | { success: true; data: WebViewParams }
  | { success: false; error: MissingParamsError } {
  try {
    return { success: true, data: parseUrlParams() };
  } catch (e) {
    if (e instanceof MissingParamsError) {
      return { success: false, error: e };
    }
    throw e;
  }
}
